import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import ThemeToggle from "../components/ThemeToggle";
import BrandLogo from "../components/BrandLogo";
import { useTheme } from "../context/ThemeContext";
import { 
  TrendingUp, 
  DollarSign, 
  ArrowRight, 
  CheckCircle,
  BarChart3,
  Link2,
  Shield
} from "lucide-react";

export default function LandingPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const features = [
    {
      icon: Link2,
      title: "Custom Referral Links",
      description: "Create trackable links with UTM parameters for every campaign you run."
    },
    {
      icon: BarChart3,
      title: "Real-Time Analytics",
      description: "Monitor clicks, signups, and conversions with detailed dashboards."
    },
    {
      icon: DollarSign,
      title: "Transparent Earnings",
      description: "Earn 10% of Maaly's fees from every merchant you refer. Track every penny."
    },
    {
      icon: Shield,
      title: "Reliable Attribution",
      description: "Your referrals are tracked accurately. Get credit for every signup."
    }
  ];

  const stats = [
    { value: "10%", label: "Commission Rate" },
    { value: "$0", label: "Minimum Payout" },
    { value: "30 Days", label: "Cookie Duration" },
    { value: "24/7", label: "Dashboard Access" }
  ];

  const faqs = [
    {
      q: "How does the affiliate program work?",
      a: "Sign up, get your unique referral link, share it with potential merchants. When they sign up and process payments through Maaly, you earn 10% of Maaly's transaction fees."
    },
    {
      q: "When do I get paid?",
      a: "Earnings become payable after a 30-day holding period. You can request payouts anytime once your balance is available."
    },
    {
      q: "What payment methods are supported?",
      a: "We support USDC (on-chain) and traditional bank transfers. Set your preferred method in your dashboard."
    },
    {
      q: "How are referrals tracked?",
      a: "When someone clicks your link, we store a cookie for 30 days. If they sign up within that period, the referral is attributed to you."
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${theme === 'dark' ? 'bg-[#011a3a]' : 'bg-[#f8fafc]'}`}>
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-xl transition-colors duration-300 ${
        theme === 'dark' 
          ? 'border-white/5 bg-[#011a3a]/80' 
          : 'border-slate-200 bg-white/80'
      }`}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <BrandLogo className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/integration" className={`text-sm transition-colors ${
              theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'
            }`}>
              Integration Docs
            </Link>
            <Button 
              variant="ghost" 
              onClick={() => navigate("/login")} 
              data-testid="login-nav-btn"
              className={theme === 'dark' ? 'text-white' : 'text-[#011a3a]'}
            >
              Sign In
            </Button>
            <Button 
              onClick={() => navigate("/signup")} 
              data-testid="signup-nav-btn" 
              className="bg-[#011a3a] text-white hover:bg-[#012169]"
            >
              Get Started
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className={`pt-32 pb-20 px-6 ${
        theme === 'dark' 
          ? 'bg-gradient-to-b from-[#0f1629] via-[#1a2744] to-[#0f1629]' 
          : 'bg-gradient-to-b from-[#e0f2fe] via-[#dbeafe] to-[#f8fafc]'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
              theme === 'dark' 
                ? 'bg-blue-500/10 border border-blue-500/20' 
                : 'bg-blue-100 border border-blue-200'
            }`}>
              <BrandLogo showMark className="w-5 h-5" />
              <span className="text-sm text-[#1E95E0]">Maaly Pay Affiliate Program</span>
            </div>
            
            <h1 className={`text-5xl md:text-6xl font-bold leading-tight ${
              theme === 'dark' ? 'text-white' : 'text-[#011a3a]'
            }`}>
              Earn commissions by referring{" "}
              <span className="text-gradient">merchants to Maaly</span>
            </h1>
            
            <p className={`mt-6 text-xl leading-relaxed ${
              theme === 'dark' ? 'text-slate-400' : 'text-slate-600'
            }`}>
              Join the Maaly affiliate program and earn 10% of transaction fees from every merchant you bring to the platform. Track your performance with real-time analytics.
            </p>
            
            <div className="mt-10 flex flex-wrap gap-4">
              <Button 
                size="lg" 
                onClick={() => navigate("/signup")} 
                data-testid="hero-signup-btn"
                className="text-lg px-8 bg-blue-600 hover:bg-blue-700 text-white"
              >
                Become an Affiliate <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                onClick={() => navigate("/login")}
                data-testid="hero-login-btn"
                className={`text-lg px-8 ${
                  theme === 'dark' 
                    ? 'border-white/20 text-white hover:bg-white/10' 
                    : 'border-slate-300 text-[#011a3a] hover:bg-slate-100'
                }`}
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className={`py-16 px-6 border-y transition-colors duration-300 ${
        theme === 'dark' ? 'border-white/5' : 'border-slate-200'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-[#011a3a]'}`}>
                  {stat.value}
                </p>
                <p className={`mt-2 text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-[#011a3a]'
            }`}>
              Everything you need to succeed
            </h2>
            <p className={`mt-4 text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
              Powerful tools to help you maximize your affiliate earnings
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div 
                key={i} 
                className={`p-6 rounded-xl border transition-all duration-300 group ${
                  theme === 'dark' 
                    ? 'bg-[#012169] border-white/5 hover:border-white/10' 
                    : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-lg'
                }`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors ${
                  theme === 'dark' 
                    ? 'bg-blue-500/10 group-hover:bg-blue-500/20' 
                    : 'bg-blue-50 group-hover:bg-blue-100'
                }`}>
                  <feature.icon className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-[#011a3a]'
                }`}>
                  {feature.title}
                </h3>
                <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className={`py-20 px-6 ${
        theme === 'dark' ? 'bg-[#012169]/50' : 'bg-slate-50'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-[#011a3a]'
            }`}>
              How it works
            </h2>
            <p className={`mt-4 text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
              Start earning in three simple steps
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Sign Up", desc: "Create your free affiliate account in seconds" },
              { step: "2", title: "Share Links", desc: "Get your unique referral links and share them" },
              { step: "3", title: "Earn Money", desc: "Earn 10% of fees from merchants you refer" }
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-xl font-bold text-white">
                    {item.step}
                  </div>
                  {i < 2 && (
                    <div className={`hidden md:block flex-1 h-px ${
                      theme === 'dark' 
                        ? 'bg-gradient-to-r from-blue-500/50 to-transparent' 
                        : 'bg-gradient-to-r from-blue-300 to-transparent'
                    }`} />
                  )}
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-[#011a3a]'
                }`}>
                  {item.title}
                </h3>
                <p className={theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-[#011a3a]'
            }`}>
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="space-y-6">
            {faqs.map((faq, i) => (
              <div 
                key={i} 
                className={`p-6 rounded-xl border transition-colors ${
                  theme === 'dark' 
                    ? 'bg-[#012169] border-white/5' 
                    : 'bg-white border-slate-200'
                }`}
              >
                <h3 className={`text-lg font-semibold mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-[#011a3a]'
                }`}>
                  {faq.q}
                </h3>
                <p className={theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}>
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className={`p-12 rounded-2xl border ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-blue-500/10 via-[#1a2744] to-transparent border-white/10' 
              : 'bg-gradient-to-br from-blue-50 via-white to-transparent border-slate-200'
          }`}>
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-[#011a3a]'
            }`}>
              Ready to start earning?
            </h2>
            <p className={`text-lg mb-8 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
              Join thousands of affiliates earning passive income with Maaly
            </p>
            <Button 
              size="lg" 
              onClick={() => navigate("/signup")}
              data-testid="cta-signup-btn"
              className="text-lg px-8 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Create Your Account <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-8 px-6 border-t transition-colors ${
        theme === 'dark' ? 'border-white/5' : 'border-slate-200'
      }`}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandLogo showMark className="w-6 h-6" />
            <span className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
              © 2026 Maaly Pay. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-6">
            <a 
              href="https://maaly.io" 
              target="_blank" 
              rel="noopener noreferrer" 
              className={`text-sm transition-colors ${
                theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              maaly.io
            </a>
            <Link 
              to="/integration" 
              className={`text-sm transition-colors ${
                theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Integration Docs
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
